import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { PushNotificationProvider } from './contexts/PushNotificationContext';

import Header from './components/common/Header';
import Footer from './components/common/Footer';
import { PageErrorBoundary } from './components/common/ErrorBoundary';
import CookieConsentManager from './components/consent/CookieConsentManager';

import LandingPage from './components/LandingPage';
import WaitlistForm from './components/WaitlistForm';
import ApplicationForm from './components/auth/ApplicationForm';

import TermsOfService from './components/legal/TermsOfService';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import CookiePolicy from './components/legal/CookiePolicy';
import FAQ from './components/support/FAQ';
import CancellationPolicy from './pages/CancellationPolicy';

import './styles/theme.css';

const PlaceholderPage = ({ title }) => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-3xl font-bold text-gray-900 mb-4">{title}</h1>
      <p className="text-gray-600">This page is coming soon.</p>
      <Link to="/" className="btn btn-primary mt-4">Back to Home</Link>
    </div>
  </div>
);

function App() {
  return (
    <Router>
      <AuthProvider>
        <SocketProvider>
          <PushNotificationProvider>
            <PageErrorBoundary>
              <div className="min-h-screen flex flex-col bg-gray-50">
                <Header />
                <main className="flex-grow">
                  <Routes>
                    {/* Public site */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/waitlist" element={<WaitlistForm />} />
                    <Route path="/apply" element={<ApplicationForm />} />

                    {/* Legal / support / info */}
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/cookies" element={<CookiePolicy />} />
                    <Route path="/cancellation-policy" element={<CancellationPolicy />} />
                    <Route path="/faqs" element={<FAQ />} />
                    <Route path="/careers" element={<PlaceholderPage title="Careers" />} />
                    <Route path="/press" element={<PlaceholderPage title="Press" />} />
                    <Route path="/help" element={<PlaceholderPage title="Help Center" />} />
                    <Route path="/safety" element={<PlaceholderPage title="Safety" />} />

                    {/* Sign-in is disabled; legacy app paths redirect to the public site */}
                    <Route path="/login" element={<Navigate to="/" replace />} />
                    <Route path="/register" element={<Navigate to="/" replace />} />
                    <Route path="/customer/*" element={<Navigate to="/waitlist" replace />} />
                    <Route path="/bodyguard/*" element={<Navigate to="/waitlist" replace />} />
                    <Route path="/admin/*" element={<Navigate to="/" replace />} />
                    <Route path="/elite/*" element={<Navigate to="/waitlist" replace />} />

                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </main>
                <Footer />
              </div>
            </PageErrorBoundary>

            <Toaster
              position="top-right"
              toastOptions={{ duration: 4000, style: { background: '#363636', color: '#fff' } }}
            />

            <CookieConsentManager />
          </PushNotificationProvider>
        </SocketProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
